/**
 * /enjoy/signature — Signature Experiences full collection page.
 *
 * Founder ruling 16 Feb 2026 PM-20: Signature Experiences are
 * curated inspiration, not search clutter. The /enjoy landing shelf
 * shows max 3 cards · the full 149-row catalogue lives HERE.
 *
 * Surface:
 *   · Hero: title + "Curated by the desk · By arrangement" subtitle
 *   · Category filter pills (Sport / Arts / Film / Music / Fashion /
 *     Society / Yachting / Collector cars / Awards / Destination / All)
 *   · Search box (title / city / whisper)
 *   · Responsive grid · every card carries:
 *       - "Curated by Concierge®" eyebrow
 *       - Title (Fraunces serif)
 *       - Category · City line
 *       - Concierge® whisper (italic)
 *       - "By arrangement" pill
 *       - "Price on request" line
 *       - "Request via Concierge® →" CTA · the card body never
 *         carries box-office / supplier-branded language.
 *   · Click a card → navigate to /enjoy?signature=<id> so the existing
 *     EventDrawer + request flow opens with the row pre-loaded.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signatureCollection } from "@/pages/enjoy/api";
import CldImg from "@/components/CldImg";
import "@/styles/enjoy.css";


// ── Top-level filter pills · founder ruling 16 Feb 2026 PM-20 ──────────
// Each pill maps to a CATEGORY_BUCKETS key on the backend (see
// backend/signature/repository.py::CATEGORY_BUCKETS).
const FILTERS = [
  { key: "all", label: "All" },
  { key: "sport", label: "Sport" },
  { key: "arts", label: "Arts" },
  { key: "film", label: "Film" },
  { key: "music", label: "Music" },
  { key: "fashion", label: "Fashion" },
  { key: "society", label: "Society & galas" },
  { key: "yachting", label: "Yachting" },
  { key: "awards", label: "Awards" },
  { key: "destination", label: "Destination" },
];


function SignatureCollectionCard({ item, onPick }) {
  const heroSrc = item.hero_image_url;
  const isPlaces = item.hero_image_source_type === "google_places";
  const credit = isPlaces && Array.isArray(item.google_photo_html_attributions)
    ? item.google_photo_html_attributions[0]
    : null;

  return (
    <button
      type="button"
      onClick={() => onPick && onPick(item)}
      data-testid={`signature-collection-card-${item.id}`}
      data-signature-id={item.id}
      style={{
        textAlign: "left", padding: 0,
        border: "1px solid var(--t-line)", borderRadius: 16,
        background: "var(--t-card, #fbf6ec)", cursor: "pointer",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        transition: "transform 200ms ease, border-color 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--t-brass, #8b6914)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--t-line)";
        e.currentTarget.style.transform = "none";
      }}
    >
      {/* Hero band */}
      <div style={{
        position: "relative",
        aspectRatio: "16 / 10",
        background: "var(--t-paper2)",
        overflow: "hidden",
      }}>
        {heroSrc ? (
          <>
            <CldImg
              src={heroSrc}
              w={720}
              alt=""
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                display: "block",
              }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            {credit && (
              <span
                data-testid={`signature-collection-credit-${item.id}`}
                className="t-mono"
                style={{
                  position: "absolute", bottom: 8, left: 10,
                  fontSize: 9, letterSpacing: "0.04em",
                  color: "#fff",
                  background: "rgba(0,0,0,0.55)",
                  backdropFilter: "blur(4px)",
                  padding: "3px 8px", borderRadius: 100,
                }}
                dangerouslySetInnerHTML={{ __html: `Photo · ${credit}` }}
              />
            )}
          </>
        ) : null}
        {/* By arrangement pill · top-right · founder ruling PM-20 */}
        <span
          data-testid={`signature-collection-byarrangement-${item.id}`}
          className="t-mono uppercase"
          style={{
            position: "absolute", top: 12, right: 12,
            fontSize: 9, letterSpacing: "0.18em",
            color: "var(--t-brass, #8b6914)", fontWeight: 600,
            background: "var(--t-brass-wash, rgba(160,122,56,0.12))",
            border: "1px solid var(--t-brass2, #d6c388)",
            padding: "3px 10px", borderRadius: 100,
          }}
        >By arrangement</span>
      </div>

      {/* Body */}
      <div style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div
          className="t-mono uppercase"
          style={{
            fontSize: 10, letterSpacing: "0.22em",
            color: "var(--t-brass, #8b6914)", fontWeight: 600,
          }}
        >Curated by Concierge®</div>
        <h3
          className="t-serif"
          style={{
            fontFamily: "Fraunces, serif", fontSize: 19,
            lineHeight: 1.25, fontWeight: 500,
            color: "var(--t-ink, #1a1614)", margin: 0,
          }}
        >{item.title}</h3>
        <p
          className="t-mono uppercase"
          style={{
            fontSize: 10, letterSpacing: "0.08em",
            color: "var(--t-ink3, #807366)", margin: 0,
          }}
        >{[item.category, item.city].filter(Boolean).join(" · ")}</p>
        {item.concierge_whisper && (
          <p
            className="t-serif"
            style={{
              fontSize: 13.5, fontStyle: "italic",
              color: "var(--t-ink2, #4f463c)", lineHeight: 1.5,
              margin: "4px 0 0",
            }}
          >&ldquo;{item.concierge_whisper.length > 150
              ? item.concierge_whisper.slice(0, 148).trim() + "…"
              : item.concierge_whisper}&rdquo;</p>
        )}
        <div style={{
          marginTop: "auto", paddingTop: 14,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8,
        }}>
          <span
            data-testid={`signature-collection-price-${item.id}`}
            className="t-mono"
            style={{
              fontSize: 11, letterSpacing: "0.08em",
              color: "var(--t-ink3, #807366)",
            }}
          >{item.indicative_price || "Price on request"}</span>
          <span
            data-testid={`signature-collection-cta-${item.id}`}
            className="t-mono uppercase"
            style={{
              fontSize: 10, letterSpacing: "0.16em",
              color: "var(--t-brass, #8b6914)", fontWeight: 600,
            }}
          >Request via Concierge® →</span>
        </div>
      </div>
    </button>
  );
}


export default function EnjoySignature() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Fetch full catalogue once (no category filter) to compute the
  // pill counts. Then filter client-side for snappy UX.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    signatureCollection({ pageSize: 200 })
      .then((r) => {
        if (cancelled) return;
        const all = (r && r.items) || [];
        setItems(all);
        setError("");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Could not load signature catalogue.");
        setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Compute per-pill counts using the SAME regex semantics as the
  // backend bucket map so the visible totals match a category fetch.
  const bucketRegex = useMemo(() => ({
    sport: /sport|motorsport|tennis|football|cycling|golf|racing|horse|american football|multi-sport|british social/i,
    arts: /\bart\b|biennale|exhibition|performing arts|opera/i,
    film: /\bfilm\b/i,
    music: /\bmusic\b/i,
    fashion: /fashion/i,
    society: /gala|society|private party|marquee experience/i,
    yachting: /yacht/i,
    awards: /awards/i,
    destination: /destination|safari|leopard|himalaya|ladakh|kutch/i,
  }), []);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const f of FILTERS) {
      if (f.key === "all") continue;
      const re = bucketRegex[f.key];
      c[f.key] = re ? items.filter((it) => re.test(it.category || "")).length : 0;
    }
    return c;
  }, [items, bucketRegex]);

  const filteredItems = useMemo(() => {
    let out = items;
    if (filter !== "all") {
      const re = bucketRegex[filter];
      if (re) out = out.filter((it) => re.test(it.category || ""));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((it) => (
        (it.title || "").toLowerCase().includes(q) ||
        (it.city || "").toLowerCase().includes(q) ||
        (it.concierge_whisper || "").toLowerCase().includes(q) ||
        (it.category || "").toLowerCase().includes(q)
      ));
    }
    return out;
  }, [items, filter, search, bucketRegex]);

  function handlePick(it) {
    // Pre-fill the /enjoy search with this title so the existing
    // EventDrawer surface opens with the matched row. Encodes a hash
    // signal so /enjoy can auto-open the drawer when desired.
    navigate(`/enjoy?signature=${encodeURIComponent(it.id)}`);
  }

  return (
    <div
      data-testid="signature-collection-page"
      style={{
        background: "var(--t-bg, #f7f0e1)",
        minHeight: "100vh",
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        color: "var(--t-ink, #1a1614)",
      }}
    >
      {/* Header */}
      <header style={{ padding: "48px 5vw 24px", maxWidth: 1320, margin: "0 auto" }}>
        <div
          className="t-mono uppercase"
          style={{
            fontSize: 11, letterSpacing: "0.24em",
            color: "var(--t-brass, #8b6914)", fontWeight: 600,
          }}
          data-testid="signature-collection-eyebrow"
        >Concierge® · enjoy · signature</div>
        <h1
          className="t-serif"
          style={{
            fontFamily: "Fraunces, serif",
            fontSize: "clamp(36px, 5vw, 56px)",
            lineHeight: 1.05, fontWeight: 500,
            margin: "12px 0 8px",
            letterSpacing: "-0.02em",
          }}
        >Signature Experiences</h1>
        <p
          data-testid="signature-collection-subtitle"
          style={{
            fontSize: 15, lineHeight: 1.55, maxWidth: 640,
            color: "var(--t-ink2, #4f463c)", margin: "0 0 8px",
          }}
        >
          <strong style={{ color: "var(--t-brass, #8b6914)" }}>Curated by the Concierge® · By arrangement.</strong>{" "}
          Hospitality, access and rooms the Concierge® has spent years building relationships
          to hold open — Wimbledon debenture days, Grand Prix paddock weekends,
          Met Gala invitations, snow-leopard tracking. Quoted by your Concierge®.
        </p>
        <button
          type="button"
          data-testid="signature-collection-back"
          onClick={() => navigate("/enjoy")}
          className="t-mono uppercase enjoy-btn ghost"
          style={{
            fontSize: 11, letterSpacing: "0.16em",
            padding: "8px 16px", marginTop: 14,
          }}
        >← Back to /enjoy</button>
      </header>

      {/* Filter pills + search */}
      <div style={{
        padding: "0 5vw 12px",
        maxWidth: 1320, margin: "0 auto",
        display: "flex", alignItems: "center", gap: 10,
        flexWrap: "wrap",
      }}>
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const n = counts[f.key] || 0;
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`signature-collection-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className="t-mono uppercase"
              style={{
                fontSize: 11, letterSpacing: "0.16em",
                padding: "8px 14px", borderRadius: 100,
                border: active ? "1px solid var(--t-brass, #8b6914)" : "1px solid var(--t-line2, #d9cdb7)",
                background: active ? "var(--t-brass, #8b6914)" : "transparent",
                color: active ? "#fff" : "var(--t-ink, #1a1614)",
                cursor: "pointer", fontWeight: 600,
              }}
            >{f.label} ({n})</button>
          );
        })}
        <input
          data-testid="signature-collection-search"
          type="search"
          placeholder="Search title, city, whisper…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            marginLeft: "auto", padding: "8px 14px",
            border: "1px solid var(--t-line2, #d9cdb7)",
            borderRadius: 100,
            background: "var(--t-card, #fbf6ec)",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            minWidth: 240,
            color: "var(--t-ink, #1a1614)",
          }}
        />
      </div>

      {/* Grid */}
      <main style={{
        padding: "20px 5vw 80px",
        maxWidth: 1320, margin: "0 auto",
      }}>
        {loading && (
          <p
            data-testid="signature-collection-loading"
            className="t-mono uppercase"
            style={{
              fontSize: 11, letterSpacing: "0.22em",
              color: "var(--t-ink3, #807366)",
              padding: "80px 0", textAlign: "center",
            }}
          >Loading the catalogue…</p>
        )}
        {error && (
          <p
            data-testid="signature-collection-error"
            style={{ color: "#7a1f10", fontSize: 14, padding: 40, textAlign: "center" }}
          >{error}</p>
        )}
        {!loading && !error && (
          <>
            <p
              data-testid="signature-collection-count"
              className="t-mono uppercase"
              style={{
                fontSize: 11, letterSpacing: "0.22em",
                color: "var(--t-ink3, #807366)",
                margin: "0 0 18px",
              }}
            >
              {filteredItems.length} {filteredItems.length === 1 ? "experience" : "experiences"}
              {filter !== "all" ? ` · ${FILTERS.find((f) => f.key === filter)?.label}` : ""}
            </p>
            <div
              data-testid="signature-collection-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: 22,
              }}
            >
              {filteredItems.map((it) => (
                <SignatureCollectionCard
                  key={it.id}
                  item={it}
                  onPick={handlePick}
                />
              ))}
            </div>
            {filteredItems.length === 0 && (
              <p
                data-testid="signature-collection-empty"
                style={{
                  fontSize: 14, color: "var(--t-ink3, #807366)",
                  padding: "60px 0", textAlign: "center",
                }}
              >
                No Signature Experiences match this filter yet.
                The Concierge® can still route a request — try a different category, or{" "}
                <button
                  onClick={() => navigate("/enjoy")}
                  style={{
                    background: "none", border: 0, padding: 0,
                    color: "var(--t-brass, #8b6914)", cursor: "pointer",
                    fontSize: 14, textDecoration: "underline",
                    textUnderlineOffset: 3,
                  }}
                >ask the Concierge®</button>.
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}
